import { useCallback, useMemo } from 'react';
import type { UserPreferences } from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import { useUserPreferencesStoreValue, useUserPreferencesStoreActions } from './context.tsx';
import type { Preferences, Value } from './types.tsx';
import { saveUserPreferenceValue, removeUserPreferenceValue } from './utils.tsx';

export const useUserPreferencesValue = (userPreferenceKey: UserPreferences) => {
	const [value, { setUserPreferenceValue, deleteUserPreferenceValue }] =
		useUserPreferencesStoreValue(userPreferenceKey);

	/**
	 * persist: boolean - if true, save the value to the server ,else just update the local state and return a promise with the new value
	 */
	const setValue = useCallback(
		(newValue: Value, persist = true) => {
			setUserPreferenceValue(userPreferenceKey, newValue);
			if (!persist) {
				return Promise.resolve(newValue);
			}
			return saveUserPreferenceValue(userPreferenceKey, newValue);
		},
		[setUserPreferenceValue, userPreferenceKey],
	);

	const deleteValue = useCallback(() => {
		deleteUserPreferenceValue(userPreferenceKey);
		return removeUserPreferenceValue(userPreferenceKey);
	}, [deleteUserPreferenceValue, userPreferenceKey]);

	const setUserPreferences = useMemo(() => ({ setValue, deleteValue }), [setValue, deleteValue]);

	return [value, setUserPreferences] as const;
};

export const useUserPreferencesActions = () => {
	const [, actions] = useUserPreferencesStoreActions();

	const userPreferenceActions = useMemo(() => {
		const setUserPreferenceValue = (userPreferenceKey: UserPreferences, value: Value) => {
			actions.setUserPreferenceValue(userPreferenceKey, value);
			saveUserPreferenceValue(userPreferenceKey, value);
		};

		const deleteUserPreferenceValue = (userPreferenceKey: UserPreferences) => {
			actions.deleteUserPreferenceValue(userPreferenceKey);
			removeUserPreferenceValue(userPreferenceKey);
		};

		const setUserPreferences = (userPreferences?: Preferences) => {
			userPreferences &&
				Object.keys(userPreferences).length !== 0 &&
				actions.setUserPreferences(userPreferences);
		};

		return {
			setUserPreferenceValue,
			setUserPreferences,
			deleteUserPreferenceValue,
			getUserPreferenceValue: actions.getUserPreferenceValue,
		};
	}, [actions]);

	return [null, userPreferenceActions] as const;
};
